import React, { useState, useEffect, useRef } from "react";

import { API_ENDPOINTS } from "../../../utils/constants";
import {
  generateChatId,
  generatedBotMessageId,
  generatedUserMessageId,
  generateTimestamp,
  MESSAGE_DIRECTIONS,
  MESSAGE_STATUSES,
} from "../../../utils/ChatManager";
import { getLanguageData } from "../../../utils/AppConstants";
import { useAppContext } from "../../../context/AppContext";
import { useSnackbar } from "../../CommonComponents/Toast/Toast";
import BotChatHead from "../../CommonComponents/ChatHeads/BotChatHead";
import botLogo from "../../../assets/images/mascot_transparent.png";
import CollapsedHeader from "../../Header/CollapsedHeader";
import ExpendedHeader from "../../Header/ExpendedHeader";
import Loader from "../../CommonComponents/ChatHeads/Loader";
import picLogo from "../../../assets/images/picLogo.png";
import chatArea from "../../../assets/images/chatarea.jpeg";
import Swal from "sweetalert2";
import useLocalization from "../../../hooks/useLocalization";
import UserChatHead from "../../CommonComponents/ChatHeads/UserChatHead";
import SuggestedQuestions from "../../CommonComponents/suggesedQuestion/SuggestedQuestions";
import { AlertCircle } from "lucide-react";
import { Trans } from "react-i18next";
import { useLogger } from "../../../utils/UnifiedLogger";
import SepratedInputChnages from "../../CommonComponents/InputUI/SepratedInputChnages";
import { useReduxChat } from "../../../hooks/useReduxChat";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  setIsTextLanguageDetecting,
  setIsContentGenerating,
  setContentGenerationData,
  setContentGenerationError,
  setIsNerDetecting,
  setNerDetectionData,
  setNerDetectionError,
  setSelectedChatId,
  setCurrentUserChatId,
  setCurrentBotChatId,
  lockVoiceFlowForChat,
  setCurrentInterimTranscript,
  setTranscriptType,
  setAccumulatedFinalTranscript,
  setDisplayTranscript,
  setRecordedBase64Data,
  lockTextFlowForChat,
  unlockTextFlowForChat,
  setIsSocketRecording,
  setGrievanceFileData,
  setGrievanceAdditionalComments,
  setIsNotRelatedToCenter,
  setForceToCenterlAuthorities,
} from "../../../store/features/message/textContentSlice";

import {
  selectIsTextLanguageDetecting,
  selectTextLanguageDetectionData,
  selectTextLanguageDetectionError,
  selectIsContentGenerating,
  selectContentGenerationData,
  selectContentGenerationError,
  selectSelectedChatId,
  selectAccumulatedFinalTranscript,
  selectCurrentInterimTranscript,
  selectSelectedSocketLanguageData,
  selectRecordedBase64Data,
  selectIsVoiceFlow,
  selectIsSocketDataReadyToSend,
  selectVoiceFlowLocks,
  selectTextFlowLocks,
  selectIsTextFlow,
  selectIsSuggestedQuestionsVisible,
  selectIsForceToCenter,
} from "../../../store/features/message/textContentSelectors";

import {
  selectInputValue,
  selectIsBigMicButtonClicked,
  selectIsNewChatClicked,
  selectIsSendTriggeredFromSocket,
  selectIsTextFromBigMicButton,
} from "../../../store/features/ui/uiSelectors";

import {
  setInputValue,
  clearInputValue,
  setInputDisabled,
  setSendButtonDisabled,
  setMicButtonDisabled,
  setIsNewChatClicked,
  setIsGrievanceRegistering,
  setIsBigMicButtonClicked,
} from "../../../store/features/ui/uiSlice";
import { updateFlowStates } from "../../../store/features/message/updateFlowStates";
import { trackApiError } from "../../../utils/ErrorTracker";
import {
  clearChatConfig,
  getChatConfig,
  updateChatConfig,
} from "../../../utils/chatConfigStorage";
import { useLanguageDetection } from "../../../hooks/useLanguageDetection";
import { resetAppChatState } from "../../../utils/appReset/resetAppChatState";

function ChatPage() {
  const apiKey = import.meta.env.VITE_BHASHINI_API_KEY;
  const NER_ENDPOINT_URL = import.meta.env.VITE_BHASHINI_NER_ENDPOINT_URL;
  const NER_SERVICE_ID = import.meta.env.VITE_BHASHINI_NER_SERVICE_ID;
  const { logInfo, logError } = useLogger("ChatPage", false);
  const dispatch = useDispatch();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isChatUpdated, setIsChatUpdated] = useState(false);
  const [isComplaintRegistering, setIsComplaintRegistering] = useState(false);
  const [showTranslitrateSuggestions, setShowTranslitrateSuggestions] =
    useState(false);
  const { showSnackbar } = useSnackbar();
  const { t, setLanguage } = useLocalization();
  const isAsciiLettersOrNumbers = /^[a-zA-Z0-9]+$/;
  const [getFormAnswers, setFormAnswers] = useState(null);
  const [getFilledForms, setFilledForms] = useState(null);
  const [onDynamicFormSubmit, setDynamicFormSubmit] = useState(false);
  const navigate = useNavigate();
  let chatConfig = getChatConfig();
  const lastMessageIdRef = useRef(null);

  // input states from Redux
  const inputValue = useSelector(selectInputValue);

  // Langauge Detection State from Redux
  const isLanguageDetecting = useSelector(selectIsTextLanguageDetecting);
  const selectedTextLanguageData = useSelector(selectTextLanguageDetectionData);
  const langDetectError = useSelector(selectTextLanguageDetectionError);
  const isBigMuttonClicked = useSelector(selectIsBigMicButtonClicked);

  // Content Generation State from Redux
  const isContentGenerating = useSelector(selectIsContentGenerating);
  const contentGenData = useSelector(selectContentGenerationData);
  const contentGenError = useSelector(selectContentGenerationError);

  const selectedChatId = useSelector(selectSelectedChatId);

  const isNewChatClicked = useSelector(selectIsNewChatClicked);
  const isTextFromBigMicButton = useSelector(selectIsTextFromBigMicButton);
  const isSendTriggeredFromSocket = useSelector(
    selectIsSendTriggeredFromSocket
  );

  const isSuggestedQuestionsVisible = useSelector(
    selectIsSuggestedQuestionsVisible
  );

  // ALL REFS
  const messagesEndRef = useRef(null);
  const hasSentRef = useRef(false);
  const botRequestRef = useRef(false);
  const isTypingRef = useRef(false);
  const prevMessageCountRef = useRef(0);
  // const isWaitingForFallbackRef = useRef(false);
  const currentRequestRef = useRef({
    // isVoiceFlow: false,
    userMessageId: null,
    botMessageId: null,
    chatId: null,
    inputText: "",
  });
  const isProcessingResponseRef = useRef(false);

  const { addMessage, updateMessage, deleteMessage, createMessage, chatList } =
    useReduxChat();

  const {
    userData,
    setIsChatStarted,
    setIsNewChat,
    setSelectedLanguage,
    setSelectedLanguageData,
    sharedApiService,
    selectedLanguageData,
    setInitialLanguage,
    isAuthenticated,
  } = useAppContext();
  const { detectLanguage } = useLanguageDetection();
  const hasCalledVoiceFallbackRef = useRef(false);

  const accumulatedFinalTranscript = useSelector(
    selectAccumulatedFinalTranscript
  );
  const currentInterimTranscript = useSelector(selectCurrentInterimTranscript);
  const selectedSocketLanguageData = useSelector(
    selectSelectedSocketLanguageData
  );
  const onRecordedAudio = useSelector(selectRecordedBase64Data);
  const isVoiceFlow = useSelector(selectIsVoiceFlow);
  const isTextFlow = useSelector(selectIsTextFlow);
  const isAudioJourneyReadyToSend = useSelector(selectIsSocketDataReadyToSend);
  const isVoiceFlowLocked = useSelector((state) =>
    selectedChatId ? selectVoiceFlowLocks(selectedChatId)(state) : false
  );
  const isTextFlowLocked = useSelector((state) =>
    selectedChatId ? selectTextFlowLocks(selectedChatId)(state) : false
  );

  const isForceToCenter = useSelector(selectIsForceToCenter);

  useEffect(() => {
    if (selectedSocketLanguageData?.code) {
      const code = selectedSocketLanguageData.code;
      setSelectedLanguageData({
        code: code,
        label: getLanguageData(code)?.label,
      });
      setSelectedLanguage(getLanguageData(code)?.label);
      setLanguage(code);
    }
  }, [
    selectedSocketLanguageData?.code,
    setSelectedLanguageData,
    setSelectedLanguage,
    setLanguage,
  ]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isForceToCenter) return;

    const runForceToCenter = async () => {
      try {
        if (isBotBusy()) return;

        const chatId = selectedChatId || generateChatId();
        dispatch(setSelectedChatId(chatId));

        const selectedChat = chatList.find((chat) => chat.chatId === chatId);
        if (!selectedChat) return;

        const firstUserMessage = selectedChat.chatMessages.find(
          (msg) => msg.direction === MESSAGE_DIRECTIONS.OUTGOING
        );

        if (!firstUserMessage?.query) return;

        const inputText = firstUserMessage.query;

        const userMessageId = generatedUserMessageId();
        const botMessageId = generatedBotMessageId();

        dispatch(setCurrentUserChatId(userMessageId));
        dispatch(setCurrentBotChatId(botMessageId));

        currentRequestRef.current = {
          userMessageId,
          botMessageId,
          chatId,
          inputText,
        };

        // ✅ Add user message
        addMessage(
          createMessage({
            content: inputText,
            userData: { name: userData.name },
            chatId,
            overrides: {
              id: userMessageId,
              timestamp: generateTimestamp(),
              status: MESSAGE_STATUSES.SENT,
              direction: MESSAGE_DIRECTIONS.OUTGOING,
              messageType: "text",
              query: inputText,
              isVoiceFlow: false,
            },
          })
        );

        // ✅ Add bot pending message
        addMessage(
          createMessage({
            content: "",
            userData: { name: "Bot" },
            chatId,
            overrides: {
              id: botMessageId,
              timestamp: generateTimestamp(),
              status: MESSAGE_STATUSES.PENDING,
              direction: MESSAGE_DIRECTIONS.INCOMING,
              isBotResponding: true,
              messageType: "text",
              query: inputText,
            },
          })
        );

        dispatch(lockTextFlowForChat(chatId));

        // =========================
        // 🔤 Detect Language
        // =========================
        let lang = getCurrentChatLanguage();

        if (!lang) {
          const langData = await detectLanguage({
            journeyType: "TEXT",
            chatId,
            query: inputText,
          });

          updateChatConfig({ language: langData });
          lang = langData;
        }

        setLanguage(lang);
        setSelectedLanguageData({
          code: lang,
          label: getLanguageData(lang)?.label,
        });

        updateLanguageForCurrentMessages(
          lang,
          t("langDetectionMessage", { lang })
        );

        // =========================
        // 🚀 CALL forceToCenter
        // =========================
        botResponseRequest({
          method: "POST",
          url: API_ENDPOINTS.forceToCenter,
          headers: {
            "Content-Type": "application/json",
            "X-Request-ID": userData.id,
            "x-source": chatId.replace(/-/g, "_"),
            "x-consumer-id": 1,
          },
          body: {
            input: {
              language: lang,
              text: inputText,
              audio: "",
              context: "JB_system_prompt",
              isVoiceFlow: false,
              userId: userData.id,
            },
            output: { format: "text" },
          },
          abortable: true,
          isEncrypted: true,

          // ⚠️ DO NOT TOUCH botRequestRef here

          onProgress: (message) => {
            updateMessage(chatId, botMessageId, {
              apiProgressData: message,
              isBotResponding: true,
            });
          },

          onSuccess: async () => {
            try {
              dispatch(setForceToCenterlAuthorities(false));

              // 🔥 IMPORTANT FIX
              // Make sure bot is NOT considered busy
              botRequestRef.current = false;
              isProcessingResponseRef.current = false;

              // Now call main bot flow
              handleContentGeneration(chatId);
            } catch (err) {
              console.error("ForceToCenter -> Main Bot Flow Error:", err);
            }
          },

          onError: (err) => {
            dispatch(setContentGenerationError(err));
          },
        });
      } catch (error) {
        console.log("FORCE_TO_CENTER_ERROR", error);
      }
    };

    runForceToCenter();
  }, [isForceToCenter]);

  useEffect(() => {
    logInfo("FLOW_STATES", "Current flow states", {
      isVoiceFlow,
      isVoiceFlowLocked,
      isTextFlow,
      isTextFlowLocked,
    });
  }, [isTextFlow, isTextFlowLocked, isVoiceFlow, isVoiceFlowLocked]);

  const botResponseRequest = sharedApiService.sendRequest;
  const nerResponseRequest = sharedApiService.sendRequest;
  const registerGrievanceRequest = sharedApiService.sendRequest;

  const enableButtonAndInputAfterResponse = (path) => {
    dispatch(setInputDisabled(false));
    dispatch(setSendButtonDisabled(false));
    dispatch(setMicButtonDisabled(false));
    if (path === "onComplete" || path === "onSuccess") {
      // dispatch(setIsContentGenerating(false));
      dispatch(setIsSocketRecording(false));
      dispatch(setIsContentGenerating(false));
      dispatch(setIsTextLanguageDetecting(false));
    }
    // console.log("Button and Input enabled after response", { path });
  };

  const handleNerDetection = () => {
    const supportedLangs = [
      "as",
      "bn",
      "gu",
      "hi",
      "kn",
      "ml",
      "te",
      "mr",
      "or",
      "pa",
      "ta",
    ];

    let selectedLang = getCurrentChatLanguage();

    if (supportedLangs.includes(selectedLang)) {
      logInfo(
        "NER_REQUEST_INITIATED",
        "Starting NER request as language is supported",
        { selectedLang }
      );
      nerResponseRequest({
        method: "POST",
        url: NER_ENDPOINT_URL,
        headers: {
          Authorization: apiKey,
          "Content-Type": "application/json",
        },
        abortable: true,
        isEncrypted: false,
        body: {
          config: {
            language: {
              sourceLanguage: selectedLang,
            },
            serviceId: NER_SERVICE_ID,
          },
          input: [
            {
              source: currentRequestRef.current.inputText,
            },
          ],
        },
        onStart: () => {
          logInfo("NER_REQUEST_STARTED", "NER request started");
          dispatch(setIsNerDetecting(true));
        },
        onCancel: (cancelInfo) => {
          logInfo(
            "BOT_REQUEST_CANCELLED",
            "Bot request was cancelled",
            cancelInfo
          );
          {
            logInfo(
              "LANG_DETECT_COMPLETE",
              "Language detection request completed"
            );
          }
          dispatch(setIsNerDetecting(false));
        },
        onSuccess: (res) => {
          logInfo("NER_REQUEST_SUCCESS", "NER request successful", res);
          dispatch(setNerDetectionData(res.output[0].nerPrediction));
          let generatedContentData = {
            nerContent: res.output[0].nerPrediction,
            status: MESSAGE_DIRECTIONS.INCOMING,
            isNerResponding: false,
          };
          updateMessage(
            selectedChatId ? selectedChatId : currentRequestRef.current.chatId,
            currentRequestRef.current.botMessageId,
            generatedContentData
          );
        },
        onError: (err) => {
          logError("NER_REQUEST_ERROR", "NER request failed", err);
          dispatch(setNerDetectionError(err));
        },
        onComplete: () => {
          dispatch(setIsNerDetecting(false));
        },
      });
    }
  };

  const handleContentGeneration = (chatId) => {
    if (isBotBusy()) {
      logInfo(
        "DUPLICATE_CALL_PREVENTED",
        "Preventing duplicate content generation call"
      );
      return;
    }

    const effectiveChatId =
      chatId ||
      currentRequestRef.current.chatId ||
      selectedChatId ||
      generateChatId();

    currentRequestRef.current.chatId = effectiveChatId;
    dispatch(setSelectedChatId(effectiveChatId));

    handleNerDetection();

    if (isVoiceJourney) {
      dispatch(lockVoiceFlowForChat(chatId));
    }

    if (isTextFlow || isTextFlowLocked) {
      dispatch(lockTextFlowForChat(chatId));
    }

    botRequestRef.current = true;
    let grievanceLanguage = getCurrentChatLanguage();

    if (!chatConfig) {
      chatConfig = getChatConfig();
    }

    // console.log("[CHAT CONFIG]: ", { chatConfig });

    let botPayload = {
      input: {
        language: grievanceLanguage,
        text: currentRequestRef.current.inputText,
        audio: "",
        context: "JB_system_prompt",
        isVoiceFlow:
          chatConfig?.journey === "VOICE"
            ? true
            : chatConfig?.journey === "TEXT"
              ? false
              : isVoiceJourney,
        is_form_data: isFormValid(getFilledForms) ? true : false,
        user_filled_forms: isFormValid(getFilledForms) ? getFilledForms : {},
        userId: userData.id,
      },
      output: { format: "text" },
    };

    // console.log("[BOT PAYLOAD -: ] ", { botPayload });

    botResponseRequest({
      method: "POST",
      url: API_ENDPOINTS.generateContent,
      headers: {
        "Content-Type": "application/json",
        "X-Request-ID": userData.id,
        "x-source": effectiveChatId.replace(/-/g, "_"),
        "x-consumer-id": 1,
      },
      body: botPayload,
      abortable: true,
      isEncrypted: true,
      enableBotStatusPolling: true,
      botStatusConfig: {
        statusUrl: `${API_ENDPOINTS.progressUpdateOnGrievance}?lang=${grievanceLanguage}`,
        pollInterval: 2000,
      },
      onStart: () => {
        dispatch(setIsContentGenerating(true));

        const generatedContentData = {
          isBotResponding: true,
        };
        updateMessage(
          selectedChatId ? selectedChatId : currentRequestRef.current.chatId,
          currentRequestRef.current.botMessageId,
          generatedContentData
        );
      },
      onProgress: (message) => {
        // FIX: Don't set isContentGenerating if request is complete
        if (!botRequestRef.current) {
          logInfo(
            "PROGRESS_AFTER_COMPLETE",
            "Ignoring progress update after completion"
          );
          return;
        }

        dispatch(setIsContentGenerating(true));
        let generatedContentData = {
          apiProgressData: message,
          isBotResponding: true,
        };
        updateMessage(
          selectedChatId ? selectedChatId : currentRequestRef.current.chatId,
          currentRequestRef.current.botMessageId,
          generatedContentData
        );
      },
      onStream: () => { },
      onSuccess: (res) => {
        logInfo("BOT_REQUEST_SUCCESS", "Bot request successful", res);
        dispatch(setContentGenerationData(res));
        dispatch(setIsContentGenerating(false));
        enableButtonAndInputAfterResponse("onSuccess");
      },
      onError: (err) => {
        // console.log("Content generation error:", err);

        dispatch(setIsContentGenerating(false));
        dispatch(setContentGenerationError(err));
        botRequestRef.current = false;
        isProcessingResponseRef.current = false;
        setTimeout(() => {
          contentGenerationError();
        }, 0);
      },
      onCancel: (cancelInfo) => {
        logInfo(
          "BOT_REQUEST_CANCELLED",
          "Bot request was cancelled",
          cancelInfo
        );
        {
          logInfo(
            "LANG_DETECT_COMPLETE",
            "Language detection request completed"
          );
        }
        dispatch(setIsContentGenerating(false));
        botRequestRef.current = false;
        isProcessingResponseRef.current = false;
      },
      onComplete: () => {
        dispatch(clearInputValue());
        dispatch(setIsContentGenerating(false));
        enableButtonAndInputAfterResponse("onComplete");
        botRequestRef.current = false;
        isProcessingResponseRef.current = false;
      },
    });
  };

  const cleanupAfterResponse = () => {
    if (isVoiceJourney) {
      dispatch(setDisplayTranscript(""));
      dispatch(setAccumulatedFinalTranscript(""));
      dispatch(setCurrentInterimTranscript(""));
      dispatch(setTranscriptType(""));
      dispatch(setRecordedBase64Data(null));
      dispatch(updateFlowStates());
    }
    hasCalledVoiceFallbackRef.current = false;
    resetChatFlowState();
  };
  const handleNewGrievance = () => {
    Swal.fire({
      title: "Start New Grievance",
      text: "Are you sure you want to start a new grievance? This will reset the current chat.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, start new",
      cancelButtonText: "No, continue",
    }).then((result) => {
      if (result.isConfirmed) {
        if (selectedChatId) {
          unlockLanguageForChat(selectedChatId);
        }
        dispatch(setSelectedChatId(null));
        dispatch(clearInputValue());
        cleanupAfterResponse();
        clearChatConfig();
      }
    });
  };

  const handleNewChat = () => {
    resetAppChatState({
      dispatch,
      sharedApiService,
      setSelectedLanguageData,
    });

    setIsNewChat(true);
    dispatch(setIsNewChatClicked(true));
    cleanupAfterResponse();
  };

  const handleSubmitGrievance = async (data, additionalData) => {
    try {
      setIsComplaintRegistering(true);
      logInfo("SUBMIT_GRIEVANCE", "User submitted a grievance", data);

      // Reset isNotRelatedToCenter when submitting grievance
      dispatch(setIsNotRelatedToCenter(false));

      // console.log("Grievance Data", { data, additionalData });

      const grivancePayload = {
        ...additionalData,
      };
      // console.log("grivancePayload", { grivancePayload });

      logInfo(
        `SUBMIT_GRIEVANCE_PAYLOAD`,
        "Grievance submission payload",
        grivancePayload
      );

      registerGrievanceRequest({
        method: "POST",
        url: API_ENDPOINTS.registerGrievance,
        headers: {
          "Content-Type": "application/json",
        },
        body: grivancePayload,
        abortable: true,
        isEncrypted: true,
        onStart: () => {
          logInfo(
            `SUBMIT_GRIEVANCE_INITIATED`,
            "Grievance submission initiated",
            grivancePayload
          );
          setIsComplaintRegistering(true);
          dispatch(setIsGrievanceRegistering(true));
        },
        onProgress: (message) => {
          logInfo(
            `SUBMIT_GRIEVANCE_PROGRESS`,
            "Grievance submission in progress",
            message
          );
        },
        onStream: () => {
          logInfo(
            `SUBMIT_GRIEVANCE_STREAM`,
            "Grievance submission stream",
            grivancePayload
          );
        },
        onSuccess: (res) => {
          setIsComplaintRegistering(false);
          dispatch(setIsGrievanceRegistering(false));
          logInfo(
            `SUBMIT_GRIEVANCE_SUCCESS`,
            "Grievance submission successful",
            res
          );

          if (res && res.status_code !== 200) {
            Swal.fire({
              icon: "error",
              title: t("grievance_submit.failTitle"),
              html: `
                              ${res && res.response_text
                  ? `<p>${typeof res.response_text === "string"
                    ? res.response_text
                    : res.response_text?.message
                  } 
                                    </br> 
                                    ${typeof res.response_text === "string"
                    ? ""
                    : res.response_text?.error
                  }</p>`
                  : `<p>${t(
                    "grievance_submit.failMessage"
                  )}</p>`
                }
                            `,
              confirmButtonText: t("grievance_submit.ok"),
              confirmButtonColor: "#f44336",
              backdrop: `rgba(0,0,0,0.6)`,
            }).then(() => {
              dispatch(setGrievanceFileData(null));
              dispatch(setGrievanceAdditionalComments(""));
            });
          } else {
            const currentChat = chatList.find((c) => c.chatId === selectedChatId);
            if (currentChat) {
                const messages = currentChat.chatMessages || [];
                const lastBotMsg = [...messages].reverse().find(m => m.direction === "incoming");
                if (lastBotMsg) {
                    updateMessage(selectedChatId, lastBotMsg.id, { isGrievanceSubmitted: true });
                }
            }

            Swal.fire({
              icon: "success",
              title: t("grievance_submit.successTitle"),
              html: `
    <div style="
        background: linear-gradient(135deg, #f0fff4, #d1fae5);
        border-radius: 16px;
        padding: 20px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        margin-top: 10px;
        animation: fadeIn 0.5s ease-in-out;
    ">
        <h3 style="
            color: #16a34a;
            font-size: 1.2rem;
            font-weight: 700;
            margin-bottom: 10px;
            text-align: center;
        ">
            🎉 ${t("grievance_submit.successMessage")}
        </h3>
        ${res && res.response_text
                  ? `
                <div style="
                    margin-top: 15px;
                    padding: 12px 16px;
                    background: linear-gradient(90deg, #dcfce7, #bbf7d0);
                    border: 1px solid #22c55e;
                    border-radius: 12px;
                    text-align: center;
                    width: 100%;
                    box-sizing: border-box;
                ">
                    <p style="
                        color: #065f46;
                        font-weight: 600;
                        margin: 0;
                        text-align: center;
                    ">
                        ${res.response_text}
                    </p>
                </div>
            `
                  : ""
                }
    </div>

    <style>
    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }
    </style>
`,
              confirmButtonText: t("grievance_submit.ok"),
              confirmButtonColor: "#4CAF50",
              backdrop: `rgba(0,0,0,0.6)`,
            }).then((result) => {
              if (result.isConfirmed) {
                handleNewChat();
              }
            });
          }
        },
        onError: (err) => {
          // console.log("Grievance submission error:", err);
          setIsComplaintRegistering(false);
          dispatch(setIsGrievanceRegistering(false));
          logError(
            `SUBMIT_GRIEVANCE_ERROR`,
            "Grievance submission failed",
            err
          );
          if (err.code === "ERR_NETWORK" || err.message.includes("Network ")) {
            Swal.fire({
              icon: "error",
              title: t("grievance_submit.failTitle"),
              html: `<p>${t("grievance_submit.failMessage")}</p>`,
              confirmButtonText: t("grievance_submit.ok"),
              confirmButtonColor: "#f44336",
              backdrop: `rgba(0,0,0,0.6)`,
            });
          }
        },
        onCancel: (cancelInfo) => {
          setIsComplaintRegistering(false);
          dispatch(setIsGrievanceRegistering(false));
          logInfo(
            `SUBMIT_GRIEVANCE_CANCELLED`,
            "Grievance submission cancelled",
            cancelInfo
          );
        },
        onComplete: () => {
          setIsComplaintRegistering(false);
          dispatch(setIsGrievanceRegistering(false));
          logInfo(
            `SUBMIT_GRIEVANCE_COMPLETED`,
            "Grievance submission completed"
          );
        },
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("grievance_submit.failTitle"),
        html: `
                    <p>${t("grievance_submit.failMessage")}</p>
                    <p>${t("grievance_submit.failSuggestion")}</p>
                `,
        confirmButtonText: t("grievance_submit.ok"),
        confirmButtonColor: "#d33",
        backdrop: `rgba(0,0,0,0.6)`,
      });
    }
  };

  /**
   * Handles search input change event.
   * @param {object} e - Event object.
   */
  const handleSearchChange = (e) => {
    logInfo("SEARCH_INPUT_CHANGED", "Search input changed", e.target.value);
    dispatch(setInputValue(e.target.value));
    dispatch(updateFlowStates());
  };

  /**
   * Handles key down event.
   * If the key pressed is "Enter", it will trigger the handleSend function.
   * @param {object} e - Event object.
   */
  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSend();
  };

  /**
   * Copies the given text to the clipboard.
   * @param {string|object} text - Text to be copied.
   * @throws {Error} - If copying to the clipboard fails.
   */
  const handleCopy = async (text) => {
    text = typeof text === "string" ? text : JSON.stringify(text);
    try {
      await navigator.clipboard.writeText(text);
      logInfo("COPY_TEXT", "User copied text", { text });
      showSnackbar(t("messageCopied"), "info");
    } catch (err) {
      console.error("Failed to copy text:", err);
      showSnackbar(t("copyFailed"), "error");
    }
  };

  const handleEdit = (query) => {
    dispatch(setInputValue(query));
    dispatch(updateFlowStates());
  };

  const handleLikeResponse = () => showSnackbar(t("responseLiked"), "success");

  const handleDisLikeResponse = () =>
    showSnackbar(t("responseDisliked"), "error");

  const handlePlayAudio = () => showSnackbar("Playing audio!", "info");

  const unlockLanguageForChat = (chatId) => {
    if (chatId) {
      dispatch(unlockTextFlowForChat(chatId));
      logInfo("LANGUAGE_UNLOCKED", "Language unlocked for chat", { chatId });
    }
  };

  // Pass unlock function to BotChatHead
  const handleUnlockLanguage = () => {
    if (selectedChatId) {
      unlockLanguageForChat(selectedChatId);
      showSnackbar(t("languageUnlocked"), "info");
    }
  };

  /**
   * Handles user's request to regenerate a response from the bot.
   * This function will generate a new user message and a new bot message with the same query as the user message,
   * and then trigger the language detection request.
   * @param {object} botChat - Bot chat object.
   * @param {object} userChat - User chat object.
   */
  //   const handleRegenerate = async (botChat, userChat) => {
  //     logInfo("REGENERATE_RESPONSE", "User requested to regenerate response", {
  //       botChat,
  //       userChat,
  //     });

  //     const userMessageId = generatedUserMessageId();
  //     const botMessageId = generatedBotMessageId();
  //     const chatId = botChat.chatId;

  //     dispatch(setSelectedChatId(chatId));
  //     dispatch(setCurrentBotChatId(botMessageId));
  //     currentRequestRef.current = {
  //       // isVoiceFlow: false,
  //       userMessageId: userMessageId,
  //       botMessageId: botMessageId,
  //       chatId: chatId,
  //       inputText: userChat.query,
  //     };

  //     const userMessage = createMessage({
  //       content: userChat.query,
  //       userData: { name: userChat.senderName },
  //       chatId: chatId,
  //       overrides: {
  //         id: userMessageId,
  //         timestamp: generateTimestamp(),
  //         status: MESSAGE_STATUSES.SENT,
  //         direction: MESSAGE_DIRECTIONS.OUTGOING,
  //         isLanguageDetecting: isVoiceJourney ? false : true,
  //         contentAudio: "",
  //         languageDetectedMessage: t("languageDetecting"),
  //       },
  //     });

  //     const botMessage = createMessage({
  //       content: "",
  //       userData: { name: botChat.senderName },
  //       chatId: chatId,
  //       overrides: {
  //         id: botMessageId,
  //         timestamp: generateTimestamp(),
  //         status: MESSAGE_STATUSES.PENDING,
  //         direction: MESSAGE_DIRECTIONS.INCOMING,
  //         contentAudio: "",
  //         languageDetectedMessage: null,
  //         isBotResponding: true,
  //         isAudioLoading: true,
  //         isLanguageDetecting: isVoiceJourney ? false : true,
  //         isErrorMessage: false,
  //         errorMessage: null,
  //         isNotRelatedToCenter: false,
  //         isNotRelatedToGrievance: false,
  //         serverRemarks: "",
  //       },
  //     });

  //     dispatch(setInputValue(userChat.query));
  //     dispatch(updateFlowStates());
  //     addMessage(userMessage);
  //     addMessage(botMessage);

  //     setIsChatStarted(true);

  //     await detectLanguage({
  //       journeyType: isVoiceJourney ? "VOICE" : "TEXT",
  //       chatId: selectedChatId, // optional
  //       query: userChat.query,
  //     });

  //     console.log("Detected language:", getCurrentChatLanguage());
  //     handleContentGeneration(selectedChatId);
  //   };
  const handleRegenerate = async (botChat, userChat) => {
    logInfo("REGENERATE_RESPONSE", "User requested regenerate", {
      botChat,
      userChat,
    });

    if (!userChat?.query) return;

    // 🔒 Cancel any in-flight requests
    sharedApiService.cancelAllRequests();

    // 🔁 Reset flow refs (same as new send)
    botRequestRef.current = false;
    isProcessingResponseRef.current = false;
    hasSentRef.current = false;

    // 🎯 Use the same chat
    const chatId = botChat.chatId;
    dispatch(setSelectedChatId(chatId));

    // 🧹 Clear voice/text leftovers
    dispatch(setAccumulatedFinalTranscript(""));
    dispatch(setCurrentInterimTranscript(""));
    dispatch(setRecordedBase64Data(null));
    dispatch(setDisplayTranscript(""));

    // ✍️ Put previous query back into input
    dispatch(setInputValue(userChat.query));
    dispatch(updateFlowStates());

    // 🧠 Sync current request ref (handleSend will overwrite IDs)
    currentRequestRef.current = {
      userMessageId: null,
      botMessageId: null,
      chatId,
      inputText: userChat.query,
    };

    // 🚀 Single source of truth
    handleSend();
  };

  const scrollToLastBotMessage = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  };

  const isVoiceJourney =
    chatConfig?.journey === "VOICE" || isVoiceFlow || isVoiceFlowLocked;

  const getCurrentChatLanguage = () => {
    const config =
      chatConfig ||
      getChatConfig(selectedChatId) ||
      getChatConfig("__PENDING__");

    if (config?.language) return config.language;

    // if (isVoiceJourney) {
    //   return selectedSocketLanguageData?.code?.trim() || "en";
    // }

    // return selectedTextLanguageData?.lang || "en";
    return null;
  };

  const getActiveChatId = () =>
    selectedChatId || currentRequestRef.current.chatId;

  const getEffectiveInputText = () => {
    const isVoiceFlow = chatConfig?.journey === "VOICE";

    const voiceText = [
      accumulatedFinalTranscript?.trim(),
      currentInterimTranscript?.trim(),
    ]
      .filter(Boolean)
      .join(" ");

    const textInput = inputValue?.trim();

    if (isVoiceFlow) {
      // 🎙️ Prefer voice, fallback to text
      return voiceText || textInput || "";
    }

    // ⌨️ Prefer text, fallback to voice
    return textInput || voiceText || "";
  };

  const updateLanguageForCurrentMessages = (lang, message) => {
    const chatId = getActiveChatId();
    if (!chatId) return;

    const payload = {
      isLanguageDetecting: false,
      contentLanguage: lang,
      languageDetectedMessage: message,
      journey: chatConfig?.journey ?? null,
      language: chatConfig?.language ?? lang,
      journeyLocked: chatConfig?.journeyLocked ?? false,
      languageLocked: chatConfig?.languageLocked ?? false,
    };

    updateMessage(chatId, currentRequestRef.current.userMessageId, payload);
    updateMessage(chatId, currentRequestRef.current.botMessageId, payload);
  };

  const isBotBusy = () =>
    botRequestRef.current || isProcessingResponseRef.current;

  const resetChatFlowState = () => {
    hasSentRef.current = false;
    botRequestRef.current = false;
    isProcessingResponseRef.current = false;
    hasCalledVoiceFallbackRef.current = false;

    currentRequestRef.current = {
      userMessageId: null,
      botMessageId: null,
      chatId: null,
      inputText: "",
    };

    dispatch(clearInputValue());
    dispatch(setAccumulatedFinalTranscript(""));
    dispatch(setCurrentInterimTranscript(""));
    dispatch(setDisplayTranscript(""));
    dispatch(setRecordedBase64Data(null));
    dispatch(setGrievanceFileData(null));
    dispatch(setGrievanceAdditionalComments(""));
  };

  const callRestASRAsFallback = async () => {
    try {
      if (!onRecordedAudio) return;

      logInfo("VOICE_FALLBACK_PRE_SEND", "Calling REST ASR before handleSend");

      await sharedApiService.sendRequest({
        method: "POST",
        url: API_ENDPOINTS.speech2text,
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          audio: onRecordedAudio,
          source_language: getCurrentChatLanguage(),
        },
        abortable: true,
        isEncrypted: false,

        onSuccess: (res) => {
          const transcript = res?.output?.trim() || res?.eng_text?.trim() || "";

          if (!transcript) return;

          dispatch(setAccumulatedFinalTranscript(transcript));
          dispatch(setCurrentInterimTranscript(""));
          dispatch(setTranscriptType("final"));
          dispatch(setInputValue(transcript));
          dispatch(updateFlowStates());

          // ✅ CRITICAL FIX
          currentRequestRef.current.inputText = transcript;

          updateMessage(
            getActiveChatId(),
            currentRequestRef.current.userMessageId,
            {
              content: transcript,
              query: transcript,
            }
          );

          logInfo("VOICE_FALLBACK_SUCCESS", "ASR injected instantly", {
            transcript,
          });
        },
      });
    } catch (err) {
      logError("VOICE_FALLBACK_FAILED", err.message);
    }
  };

  const handleSend = async () => {
    dispatch(updateFlowStates());
    dispatch(setIsBigMicButtonClicked(false));

    // Reset isNotRelatedToCenter when user submits a new query
    dispatch(setIsNotRelatedToCenter(false));

    // console.log("HANDLE_SEND", "User triggered handleSend", {
    //   isVoiceFlow,
    //   // onRecordedAudio,
    //   currentInterimTranscript,
    //   inputValue,
    // });

    // ✅ Track if we need to clear audio
    let shouldClearAudio = false;
    let effectiveAudioData = onRecordedAudio;

    // Clear audio if text is typed but transcripts are empty
    if (
      inputValue.trim() &&
      !currentInterimTranscript.trim() &&
      !accumulatedFinalTranscript.trim() &&
      onRecordedAudio
    ) {
      // console.log(
      //   "CLEARING_STALE_AUDIO",
      //   "Clearing audio data as text input is present without transcripts"
      // );
      dispatch(setRecordedBase64Data(null));
      shouldClearAudio = true;
      effectiveAudioData = null; // Use null for validation
    }

    // Clear audio if no input at all but audio exists
    if (
      !inputValue.trim() &&
      !currentInterimTranscript.trim() &&
      !accumulatedFinalTranscript.trim() &&
      onRecordedAudio
    ) {
      // console.log(
      //   "CLEARING_STALE_AUDIO",
      //   "Clearing audio data as no text input present"
      // );
      dispatch(setRecordedBase64Data(null));
      shouldClearAudio = true;
      effectiveAudioData = null; // Use null for validation
    }

    const isInputValid =
      (!effectiveAudioData && inputValue.trim()) ||
      (effectiveAudioData &&
        (currentInterimTranscript.trim() !== "" ||
          accumulatedFinalTranscript.trim() !== ""));

    if (!isInputValid) {
      // console.log("HANDLE_SEND_INVALID", "Invalid input, not sending", {
      //   hasAudio: !!effectiveAudioData,
      //   inputValue: inputValue?.substring(0, 20),
      //   transcript: currentInterimTranscript?.substring(0, 20),
      // });
      return;
    }

    const chatId = selectedChatId !== null ? selectedChatId : generateChatId();
    const inputText = getEffectiveInputText();
    currentRequestRef.current.inputText = inputText;

    if (
      isVoiceJourney &&
      effectiveAudioData &&
      !hasCalledVoiceFallbackRef.current
    ) {
      hasCalledVoiceFallbackRef.current = true;
      // isWaitingForFallbackRef.current = true;
      // if (!isTextFromBigMicButton) {
      //   callRestASRAsFallback().finally(() => {
      //     isWaitingForFallbackRef.current = false;

      //     // ⬇️ Trigger bot only AFTER fallback finishes
      //     handleContentGeneration(chatId);
      //   });
      // } else {
      //   isWaitingForFallbackRef.current = false;
      //   handleContentGeneration(chatId);
      // }

      // isWaitingForFallbackRef.current = false;
      handleContentGeneration(chatId);
    }

    setIsChatUpdated(false);

    currentRequestRef.current.chatId = chatId;
    dispatch(setSelectedChatId(chatId));
    const pendingConfig = getChatConfig("__PENDING__");

    if (pendingConfig?.language) {
      updateChatConfig({
        chatId,
        language: pendingConfig.language,
        languageLocked: true,
        journey: pendingConfig.journey,
        journeyLocked: true,
      });
    }

    setFilledForms(null);
    // console.log("CHAT_ID", "Using chat ID", { chatId, selectedChatId });

    const userMessageId = generatedUserMessageId();
    const botMessageId = generatedBotMessageId();
    dispatch(setCurrentBotChatId(botMessageId));
    dispatch(setCurrentUserChatId(userMessageId));
    dispatch(setInputDisabled(true));
    dispatch(setSendButtonDisabled(true));
    dispatch(setMicButtonDisabled(true));

    // console.log("CHECK CHAT TEXT : ", {
    //   inputText,
    //   isVoiceJourney,
    //   getActiveChatId: getActiveChatId(),
    //   language: getCurrentChatLanguage(),
    // });

    currentRequestRef.current = {
      shouldAutoPlay: isVoiceFlow,
      userMessageId: userMessageId,
      botMessageId: botMessageId,
      chatId: chatId,
      inputText: inputText,
    };

    // console.log("MESSAGE_CREATION", "Creating user and bot messages", {
    //   isVoiceFlow,
    //   hasAudio: !!effectiveAudioData,
    //   transcript: accumulatedFinalTranscript?.substring(0, 30),
    //   inputValue: inputText,
    // });
    const userMessage = createMessage({
      content: inputText,
      userData: { name: userData.name },
      chatId,
      overrides: {
        id: userMessageId,
        timestamp: generateTimestamp(),
        status: MESSAGE_STATUSES.SENT,
        direction: MESSAGE_DIRECTIONS.OUTGOING,
        isLanguageDetecting: isVoiceJourney ? false : true,
        contentAudio: isVoiceFlow ? effectiveAudioData : "",
        languageDetectedMessage: t("languageDetecting"),
        messageType: "text",
        query: inputText,
        isVoiceFlow: isVoiceJourney,
        isVoiceFlowLocked: isVoiceFlowLocked ? true : false,
      },
    });

    const botMessage = createMessage({
      content: "",
      userData: { name: "Bot" },
      chatId,
      overrides: {
        id: botMessageId,
        timestamp: generateTimestamp(),
        status: MESSAGE_STATUSES.PENDING,
        direction: MESSAGE_DIRECTIONS.INCOMING,
        contentAudio: "",
        languageDetectedMessage: null,
        isBotResponding: true,
        isAudioLoading: false,
        isLanguageDetecting: isVoiceJourney ? false : true,
        query: inputText,
        isVoiceFlow: isVoiceJourney,
        isTextFlow: !isVoiceJourney,
        isVoiceFlowLocked: isVoiceFlowLocked ? true : false,
        shouldAutoPlay: isVoiceFlow,
      },
    });

    // addMessage(userMessage);
    // addMessage(botMessage);
    // setIsChatStarted(true);
    addMessage(userMessage);
    addMessage(botMessage);
    setIsChatStarted(true);
    setTimeout(() => scrollToLastBotMessage(), 100);

    try {
      if (!isVoiceJourney) {
        let lang = getCurrentChatLanguage();
        if (!lang) {
          // console.log("Language not exists");
          let langData = await detectLanguage({
            journeyType: "TEXT",
            chatId: getActiveChatId(), // optional
            query: inputText,
          });
          // console.log("Detected language data:", langData);
          updateChatConfig({
            language: langData,
          });
          lang = getCurrentChatLanguage();
        }
        setLanguage(lang);
        setSelectedLanguageData({
          code: lang,
          label: getLanguageData(lang)?.label,
        });
        const defaultLangMsg = t("langDetectionMessage", {
          lang: lang,
          defaultValue: t("langDetectionMessage", {
            lang: lang,
          }),
        });
        updateLanguageForCurrentMessages(lang, defaultLangMsg);
        handleContentGeneration(getActiveChatId());
      } else {
        // console.log("VOICE_FLOW_LANG", "Using language from voice detection", {
        //   lang: getCurrentChatLanguage(),
        // });

        const lang = getCurrentChatLanguage();

        const defaultLangMsg = t("langDetectionMessage", {
          lang: lang,
          defaultValue: t("langDetectionMessage", { lang: lang }),
        });

        setSelectedLanguageData({
          code: lang,
          label: getLanguageData(lang)?.label,
        });
        setSelectedLanguage(getLanguageData(lang)?.label);
        setLanguage(lang);

        // ✅  FIX: Handle locked language case
        let languageDetectedMessageToUse = defaultLangMsg;

        if (isTextFlowLocked && selectedTextLanguageData?.lang) {
          const lockedLang = selectedTextLanguageData.lang;
          languageDetectedMessageToUse = t("langDetectionMessage", {
            lang: lockedLang,
            defaultValue: t("langDetectionMessage", { lang: lockedLang }),
          });
          // console.log(
          //   "USING_LOCKED_LANGUAGE_MESSAGE",
          //   "Using locked language message",
          //   {
          //     lockedLang,
          //     message: languageDetectedMessageToUse,
          //   }
          // );
        }

        updateLanguageForCurrentMessages(lang, languageDetectedMessageToUse);

        // if (isVoiceJourney) {
        //   if (!isWaitingForFallbackRef.current) {
        //     handleContentGeneration(chatId);
        //   }
        // } else {
        //   handleContentGeneration(chatId);
        // }
        handleContentGeneration(chatId);
      }
    } catch (error) {
      console.error("Error in handleSend:", error);
      updateMessage(chatId, botMessageId, {
        content: t("cannotUnderstand"),
        status: MESSAGE_DIRECTIONS.INCOMING,
        isBotResponding: false,
      });
    }
  };

  const onSuggestedItemClick = (data) => {
    dispatch(
      setInputValue((prev) => {
        const lastSpaceIndex = prev.trim().lastIndexOf(" ");
        const newText = prev.slice(0, lastSpaceIndex) + " " + data;
        return newText.trim();
      })
    );
    dispatch(updateFlowStates());

    setShowTranslitrateSuggestions(false);
  };

  useEffect(() => {
    return () => {
      resetChatFlowState();
      cleanupAfterResponse();
      resetAppChatState({
        dispatch,
        sharedApiService,
        setSelectedLanguageData,
      });
    };
  }, []);

  useEffect(() => {
    if (isNewChatClicked) {
      // Clear all refs
      hasSentRef.current = false;
      botRequestRef.current = false;
      isTypingRef.current = false;
      prevMessageCountRef.current = 0;

      currentRequestRef.current = {
        userMessageId: null,
        botMessageId: null,
        chatId: null,
        inputText: "",
      };

      resetChatFlowState();

      // Reset the flag
      dispatch(setIsNewChatClicked(false));

      logInfo("NEW_CHAT_CLEANUP", "Cleaned up state for new chat");
    }
  }, [isNewChatClicked]);

  const sendApiErrorToServer = (
    message,
    responseData,
    errorInfo,
    inputQuery
  ) => {
    return trackApiError({
      message: message,
      status: 0,
      statusText: "React Backend API Error",
      url: window.location.href,
      method: "API_CALL_ERROR",
      responseData: responseData,
      requestId: null,
      duration: null,
      attempts: 1,
      timeout: null,
      curl: "N/A",
      headers: null,
      requestData: inputQuery,
      component: "ChatPage",
      errorInfo: errorInfo,
      timestamp: new Date().toISOString(),
    });
  };

  const contentGenerationError = () => {
    const generatedContentData = {
      isError: true,
      isBotResponding: false,
      isLanguageDetecting: false,
      isAudioLoading: false,
      status: MESSAGE_DIRECTIONS.INCOMING,
      timestamp: generateTimestamp(),
    };
    updateMessage(
      selectedChatId,
      currentRequestRef.current.botMessageId,
      generatedContentData
    );

    sendApiErrorToServer(
      "Content Generation Error",
      contentGenError,
      { errorMessage: contentGenError?.message },
      currentRequestRef.current.inputText
    );

    cleanupAfterResponse();
    setIsChatUpdated(true);
  };

  useEffect(() => {
    if (!currentRequestRef.current.botMessageId || !selectedChatId) {
      return;
    }

    // CRITICAL: Skip if we're already processing or if botRequest is complete
    if (isProcessingResponseRef.current || !botRequestRef.current) {
      return;
    }

    if (isContentGenerating) {
      const generatedContentData = {
        isBotResponding: true,
      };
      updateMessage(
        selectedChatId,
        currentRequestRef.current.botMessageId,
        generatedContentData
      );
      setIsChatUpdated(true);
    } else if (contentGenError) {
      isProcessingResponseRef.current = true;
      showSnackbar(t("contentGenerationFailed"), "error");
      setTimeout(() => {
        contentGenerationError();
      }, 0);
    } else if (contentGenData) {
      isProcessingResponseRef.current = true;
      try {
        let botMessage = null;
        let isErrorMessage = contentGenData["isErrorMessage"] || false;
        if (isErrorMessage) {
          let errorMessage = contentGenData["errorMessage"] || "";
          botMessage = {
            isErrorMessage: true,
            errorMessage: errorMessage,
            status: MESSAGE_DIRECTIONS.INCOMING,
            isBotResponding: false,
            isAudioLoading: false,
            messageType: "text",
            timestamp: generateTimestamp(),
          };
          sendApiErrorToServer(
            "Content Generation Error from API",
            contentGenData,
            { errorMessage },
            currentRequestRef.current.inputText
          );
        } else {
          let isNotRelatedToGrievance =
            contentGenData["isNotRelatedToGrievance"] || false;
          let isNotSubcategory = contentGenData["isNotSubcategory"] || false;
          let regionalResponse = contentGenData["Regional_Response"];
          let standardResponse = contentGenData["response"];
          let grievanceBucket = {};
          let serverRemarks = contentGenData["server_remarks"] || "";

          const stateName = contentGenData["stateName"];
          const stateCode = contentGenData["stateCode"];
          const hasValidState =
            typeof stateName === "string" &&
            stateName.trim() !== "" &&
            typeof stateCode === "string" &&
            stateCode.trim() !== "";
          let isNotRelatedToCenter = hasValidState
            ? Boolean(contentGenData["isNotRelatedToCenter"])
            : false;

          try {
            // gardrail check
            if (isNotRelatedToGrievance) {
              botMessage = {
                content: regionalResponse,
                contentStandard: standardResponse,
                contentAudio: contentGenData["audio"],
                botResponse: regionalResponse,
                status: MESSAGE_DIRECTIONS.INCOMING,
                isBotResponding: false,
                isAudioLoading: false,
                messageType: "text",
                timestamp: generateTimestamp(),
                isNotRelatedToGrievance: isNotRelatedToGrievance,
                isNotSubcategory: isNotSubcategory,
              };
              // state check
            } else if (isNotRelatedToCenter) {
              botMessage = {
                content: regionalResponse,
                contentStandard: standardResponse,
                contentAudio: contentGenData["audio"],
                serverRemarks: serverRemarks,
                user_query: contentGenData["user_query"] || "",
                stateName: contentGenData["stateName"] || "",
                stateCode: contentGenData["stateCode"] || "",
                stateName_regional: contentGenData["stateName_regional"] || "",
                botResponse: regionalResponse,
                status: MESSAGE_DIRECTIONS.INCOMING,
                isBotResponding: false,
                isAudioLoading: false,
                messageType: "text",
                timestamp: generateTimestamp(),
                isNotRelatedToCenter: isNotRelatedToCenter,
                isNotSubcategory: isNotSubcategory,
              };
              dispatch(setIsNotRelatedToCenter(true));
            } else {
              if (typeof regionalResponse === "string") {
                const trimmed = regionalResponse.trim();
                if (trimmed.startsWith("{")) {
                  const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
                  regionalResponse = parsed;
                  grievanceBucket = parsed?.grievance_bucket || {};
                }
              } else if (
                typeof regionalResponse === "object" &&
                regionalResponse !== null
              ) {
                grievanceBucket = regionalResponse?.grievance_bucket || {};
              }

              if (typeof standardResponse === "string") {
                const trimmed = standardResponse.trim();
                if (trimmed.startsWith("{")) {
                  const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
                  standardResponse = parsed;
                  grievanceBucket = parsed?.grievance_bucket || {};
                }
              } else if (
                typeof standardResponse === "object" &&
                standardResponse !== null
              ) {
                grievanceBucket = standardResponse?.grievance_bucket || {};
              }

              // SUCCESS CASE ERROR LOGGING CONDITIONS
              const isRegionalEmpty =
                typeof regionalResponse === "string" &&
                (regionalResponse.trim() === "" ||
                  (regionalResponse.trim().toLowerCase() ===
                    "only form fields" &&
                    contentGenData.field_form?.length === 0));

              const isStandardEmpty =
                typeof standardResponse === "string" &&
                (standardResponse.trim() === "" ||
                  (standardResponse.trim().toLowerCase() ===
                    "only form fields" &&
                    contentGenData.field_form?.length === 0));

              if (isRegionalEmpty || isStandardEmpty) {
                sendApiErrorToServer(
                  "Content Generation Error from API",
                  contentGenData,
                  { errorMessage: "Empty response from bot" },
                  currentRequestRef.current.inputText
                );
              }

              botMessage = {
                content: regionalResponse,
                contentStandard: standardResponse,
                contentAudio: contentGenData["audio"],
                field_form: contentGenData["field_form"] || {},
                category_code: contentGenData["category_code"] || null,
                org_code: contentGenData["org_code"] || "",
                field_form_list: contentGenData["field_form_list"] || [],
                mandatory_data_present:
                  contentGenData["mandatory_data_present"],
                mandatory_questions_answered:
                  contentGenData["mandatory_questions_answered"],
                mandatory_questions_asked:
                  contentGenData["mandatory_questions_asked"],
                grievance_bucket: grievanceBucket,
                botResponse: regionalResponse,
                updated_grievance: contentGenData["updated_grievance"] || "",
                status: MESSAGE_DIRECTIONS.INCOMING,
                isBotResponding: false,
                isAudioLoading: false,
                messageType: "text",
                timestamp: generateTimestamp(),
                isNotRelatedToGrievance: isNotRelatedToGrievance,
                serverRemarks: serverRemarks,
                isNotSubcategory: isNotSubcategory,
              };
            }
          } catch (err) {
            logError(
              "REGIONAL_RESPONSE_PARSE_ERROR",
              "Failed to parse Regional_Response",
              err
            );
            sendApiErrorToServer(
              "Content Generation Error from API",
              contentGenData,
              { errorMessage: "Failed to parse Regional_Response" },
              currentRequestRef.current.inputText
            );
          }
        }

        updateMessage(
          selectedChatId,
          currentRequestRef.current.botMessageId,
          botMessage
        );
        cleanupAfterResponse();
        setIsChatUpdated(true);
      } catch (err) {
        logError(
          "CONTENT_GEN_PROCESSING_ERROR",
          "Error processing content generation data",
          err
        );
        sendApiErrorToServer(
          "Content Generation Processing Error",
          contentGenData,
          { errorMessage: err.message },
          currentRequestRef.current.inputText
        );
      }
    } else if (
      contentGenData === null &&
      currentRequestRef.current.botMessageId
    ) {
      let generatedContentData = {
        content: t("noResponse"),
        status: MESSAGE_DIRECTIONS.INCOMING,
        isBotResponding: false,
        isAudioLoading: true,
      };
      updateMessage(
        selectedChatId,
        currentRequestRef.current.botMessageId,
        generatedContentData
      );
    }
    // enableButtonAndInputAfterResponse("useEffect");
  }, [isContentGenerating, contentGenData, contentGenError]);

  function isFormValid(formData) {
    if (!formData?.field_form?.InputFields) return false;

    const inputFields = formData.field_form.InputFields;

    for (const field of inputFields) {
      if (field.IsHidden) continue;

      if (field.IsMandatory) {
        if (field.FieldType === "Dropdown") {
          if (!field.UserInputValue || !field.UserInputText) {
            console.warn(`❌ Missing dropdown value for ${field.FieldName}`);
            return false;
          }
        } else {
          if (
            field.UserInputValue === null ||
            field.UserInputValue === undefined ||
            field.UserInputValue === ""
          ) {
            console.warn(`❌ Missing value for ${field.FieldName}`);
            return false;
          }
        }
      }
    }
    return true;
  }

  useEffect(() => {
    if (
      onDynamicFormSubmit &&
      getFormAnswers &&
      Object.keys(getFormAnswers).length > 0
    ) {
      dispatch(setInputValue(Object.values(getFormAnswers).join(",")));
      dispatch(updateFlowStates());
      logInfo("DYNAMIC_FORM_ANSWERS", "Dynamic form answers", {
        getFilledForms,
      });
      logInfo(
        "DYNAMIC_FORM_ANSWERS",
        "Dynamic form answers set to input value",
        { getFormAnswers }
      );
      if (isFormValid(getFilledForms)) {
        logInfo("DYNAMIC_FORM_VALIDATION", "Dynamic form is valid", {
          getFilledForms,
        });
      } else {
        logInfo("DYNAMIC_FORM_VALIDATION", "Dynamic form is not valid", {
          getFilledForms,
        });
      }
    }
  }, [onDynamicFormSubmit]);

  useEffect(() => {
    if (isVoiceJourney) {
      // set instant input value from transcripts
      dispatch(
        setInputValue(
          currentInterimTranscript || accumulatedFinalTranscript || ""
        )
      );
      // Combine exactly like the display shows
      const combinedTranscript = [
        accumulatedFinalTranscript?.trim(),
        currentInterimTranscript?.trim(),
      ]
        .filter(Boolean)
        .join(" ");

      if (combinedTranscript.trim()) {
        dispatch(setInputValue(combinedTranscript));
        // dispatch(updateFlowStates());
        logInfo("VOICE_TRANSCRIPT_UPDATE", "Voice transcript updated", {
          final: accumulatedFinalTranscript?.substring(0, 50),
          interim: currentInterimTranscript?.substring(0, 50),
          combined: combinedTranscript.substring(0, 100),
        });
      }
    }
  }, [
    isVoiceFlow,
    isVoiceFlowLocked,
    accumulatedFinalTranscript,
    currentInterimTranscript,
  ]);

  useEffect(() => {
    if (onDynamicFormSubmit && inputValue && inputValue.trim() !== "") {
      handleSend();
      setDynamicFormSubmit(false);
      logInfo("DYNAMIC_FORM_SUBMIT", "Dynamic form submitted with answers", {
        getFormAnswers,
        inputValue,
      });
    }
  }, [inputValue, onDynamicFormSubmit]);

  // useEffect(() => {
  //   if (!selectedChatId) return;

  //   const selectedChat = chatList.find((c) => c.chatId === selectedChatId);
  //   if (!selectedChat) return;

  //   const messages = selectedChat.chatMessages || [];
  //   const messageCount = messages.length;
  //   const lastMessage = messages.at(-1);

  //   // Skip scrolling if user is typing
  //   if (isTypingRef?.current) {
  //     logInfo("ChatPage.SKIP_SCROLL", "User typing, skipping scroll");
  //     return;
  //   }

  //   // Scroll only if a new message is added
  //   if (messageCount > prevMessageCountRef.current) {
  //     const isIncomingMessage =
  //       lastMessage?.direction === MESSAGE_DIRECTIONS.INCOMING ||
  //       lastMessage?.direction === MESSAGE_DIRECTIONS.OUTGOING;

  //     if (isIncomingMessage) {
  //       setTimeout(() => scrollToLastBotMessage(), 100);
  //     }
  //   }

  //   // Update previous message count
  //   prevMessageCountRef.current = messageCount;
  // }, [chatList, selectedChatId]);

  // useEffect(() => {
  //   const selectedChat = chatList.find((c) => c.chatId === selectedChatId);
  //   if (!selectedChat) return;

  //   const messages = selectedChat.chatMessages || [];
  //   const lastMessage = messages.at(-1);

  //   if (!lastMessage) return;

  //   // 🚫 If same last message, do nothing
  //   if (lastMessageIdRef.current === lastMessage.id) {
  //     return;
  //   }

  //   lastMessageIdRef.current = lastMessage.id;

  //   // ✅ Scroll only when a new message appears
  //   setTimeout(() => {
  //     scrollToLastBotMessage();
  //   }, 200);
  // }, [chatList, selectedChatId]);
  useEffect(() => {
    const selectedChat = chatList.find((c) => c.chatId === selectedChatId);
    if (!selectedChat) return;

    const messages = selectedChat.chatMessages || [];
    const lastMessage = messages.at(-1);

    if (!lastMessage) return;

    // 🚫 If same last message, do nothing
    if (lastMessageIdRef.current === lastMessage.id) {
      return;
    }

    lastMessageIdRef.current = lastMessage.id;

    // ✅ Scroll only when a new message appears
    setTimeout(() => {
      scrollToLastBotMessage();
    }, 200);
  }, [chatList, selectedChatId]);

  useEffect(() => {
    dispatch(updateFlowStates());
  }, [
    inputValue,
    onRecordedAudio,
    accumulatedFinalTranscript,
    currentInterimTranscript,
    dispatch,
  ]);

  const hasActiveChat = Boolean(chatList && selectedChatId);

  const selectedChat = hasActiveChat
    ? chatList.find((chat) => chat.chatId === selectedChatId)
    : null;

  const chatMessages = selectedChat?.chatMessages || [];
  const lastMessage = chatMessages.at(-1);

  const isMandatoryFlow = lastMessage?.mandatory_data_present === true;

  return (
    <>
      {isComplaintRegistering && (
        <Loader message={t("registerningGrievance")} />
      )}
      <div
        className="w-full h-full relative shadow-md flex flex-col overflow-hidden transition-all duration-300 ease-in-out bg-[#F8F9FA]"
      >
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.50] bg-no-repeat bg-top bg-[length:100%_100%] md:bg-[center_bottom] md:bg-cover"
          style={{
            backgroundImage: `url(${chatArea})`,
            transform: 'scale(1.0)',
            transformOrigin: 'bottom center'
          }}
        />
        <div className="relative z-[50] shadow-sm">
          {selectedChatId ? <CollapsedHeader /> : <ExpendedHeader />}
        </div>

        {!selectedChatId && (
          <div className="relative overflow-y-auto">
            <SuggestedQuestions
              questions={t("suggestedQuestions.suggestedQuestions", {
                returnObjects: true,
              })}
              onSuggestedQuestionClick={(question) => {
                dispatch(setInputValue(question));
                dispatch(updateFlowStates());
              }}
              handleSend={handleSend}
            />
          </div>
        )}

        <div className="relative flex-1 overflow-y-auto bg-cover bg-center z-0">
          <div className="relative z-10 px-4 py-2 flex flex-col space-y-3 bg-[#] dark:bg-[#3C4043] transition-all duration-300 ease-in-out">
            {((chatList && selectedChatId) || isChatUpdated) &&
              chatList
                .find((chat) => chat.chatId === selectedChatId)
                ?.chatMessages?.map((chat, index, array) => {
                  //   const { contentAudio, ...chatWithoutAudio } = chat;

                  //   console.log("Rendering chat message:", {
                  //     chat: chatWithoutAudio,
                  //   });

                  let isLastItem = index === array.length - 1;
                  return chat.direction === "outgoing" ? (
                    <UserChatHead
                      chatData={chat}
                      id={`${selectedChatId}_${chat.id}`}
                      key={`user-${chat.id}`}
                      message={{ text: chat.query, audio: chat.contentAudio }}
                      timestamp={chat.timestamp}
                      onCopy={() => handleCopy(chat.query)}
                      onEdit={() => handleEdit(chat.query)}
                      userImage={picLogo}
                      identificationClassName={`${selectedChatId}_${chat.id}`}
                    />
                  ) : (
                    <BotChatHead
                      identificationClassName={`${selectedChatId}_${chat.id}`}
                      id={`${selectedChatId}_${chat.id}`}
                      key={
                        chat.contentAudio
                          ? `bot-${index}-${chat.contentAudio?.slice(0, 10)}`
                          : `bot-${index}`
                      }
                      response={{
                        text: chat.content,
                        contentStandard: chat.contentStandard,
                        audioUrl: chat.audioUrl,
                        imageUrl: chat.imageUrl,
                        contentAudio: chat.contentAudio,
                        isLanguageDetecting: chat.isLanguageDetecting,
                        isBotResponding: chat.isBotResponding,
                        languageDetectedMessage: chat.languageDetectedMessage,
                        mandatory_data_present: chat.mandatory_data_present,
                        mandatory_questions_answered:
                          chat.mandatory_questions_answered,
                        mandatory_questions_asked:
                          chat.mandatory_questions_asked,
                        grievance_bucket: chat.grievance_bucket,
                        isError: chat.isError || false,
                        botResponse: chat.botResponse || {},
                        updated_grievance: chat.updated_grievance || "",
                        field_form: chat.field_form || [],
                        isVoiceFlow: chat.isVoiceFlow || false,
                        isTextFlow: chat.isTextFlow || false,
                        isVoiceFlowLocked: chat.isVoiceFlowLocked || false,
                        nerContent: chat.nerContent,
                        isNerResponding: chat.isNerResponding,
                        apiProgressData: chat.apiProgressData || null,
                        category_code: chat.category_code || null,
                        org_code: chat.org_code || "",
                        field_form_list: chat.field_form_list || [],
                        errorMessage: chat.errorMessage,
                        isErrorMessage: chat.isErrorMessage,
                        isNotRelatedToGrievance: chat.isNotRelatedToGrievance,
                        isNotRelatedToCenter: chat.isNotRelatedToCenter,
                        serverRemarks: chat.serverRemarks,
                        user_query: chat.user_query,
                        stateName: chat.stateName,
                        stateName_regional: chat.stateName_regional,
                        stateCode: chat.stateCode,
                        isNotSubcategory: chat.isNotSubcategory,
                        isGrievanceSubmitted: chat.isGrievanceSubmitted || false,
                      }}
                      isMostRecent={
                        isLastItem &&
                        (chat.isVoiceFlow || chat.isVoiceFlowLocked)
                      }
                      chatData={chat}
                      timestamp={chat.timestamp}
                      botImage={botLogo}
                      onCopy={() => handleCopy(chat.content)}
                      onLike={handleLikeResponse}
                      onDislike={handleDisLikeResponse}
                      onPlayAudio={handlePlayAudio}
                      onEdit={handleEdit}
                      onDelete={() => deleteMessage(chat.id)}
                      onRegenerate={() => {
                        const currentChatList =
                          chatList.find((c) => c.chatId === selectedChatId)
                            ?.chatMessages || [];
                        const currentIndex = currentChatList.findIndex(
                          (msg) => msg.id === chat.id
                        );
                        const userMessage = currentChatList
                          .slice(0, currentIndex)
                          .reverse()
                          .find((msg) => msg.direction === "outgoing");

                        handleRegenerate(chat, userMessage);
                      }}
                      setHasSubmitted={setHasSubmitted}
                      hasSubmitted={hasSubmitted}
                      handleSubmitGrievance={handleSubmitGrievance}
                      setFormAnswers={setFormAnswers}
                      setFilledForms={setFilledForms}
                      onDynamicFormSubmit={onDynamicFormSubmit}
                      setDynamicFormSubmit={setDynamicFormSubmit}
                      handleSend={handleSend}
                      onUnlockLanguage={handleUnlockLanguage}
                      isLanguageLocked={isTextFlowLocked}
                    />
                  );
                })}
          </div>
          <div id="fakeView" ref={messagesEndRef} />
        </div>

        <div className="h-[46px]"></div>
        {/* This prevents SocketMicStreamer from unmounting during recording */}
        {hasActiveChat && isMandatoryFlow ? (
          <div className="bg-[#F5F7FF] border border-[rgba(18,51,168,0.3)] text-blue-800 rounded-md p-2 mx-2 shadow-md text-center mb-2">
            <div className="flex flex-col items-center justify-center space-y-1">
              <AlertCircle className="w-7 h-7 text-[#1233A8]" />
              <p className="text-sm text-gray-700 max-w-xxl">
                <Trans
                  i18nKey="grievanceSuccess"
                  components={{ 1: <span className="font-semibold" /> }}
                />
              </p>
            </div>
          </div>
        ) : (
          <>
            {isBigMuttonClicked ? null : (
              <SepratedInputChnages
                isUserInputBar={true}
                isTypingRef={isTypingRef}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                handleSend={handleSend}
                onSuggestedItemClick={onSuggestedItemClick}
                placeholder={t("inputPlaceholder")}
                sendClicked={
                  getFormAnswers !== null && getFormAnswers.length > 0
                }
                showTranslitrateSuggestions={showTranslitrateSuggestions}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}

export default ChatPage;
